import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  CheckCircle2, XCircle, Edit3, Loader2, FileText, 
  MapPin, Calendar, DollarSign, AlertCircle, ArrowLeft,
  Building2, Phone, Mail
} from 'lucide-react';
import { formatDate, formatDateTime, formatTime } from './utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function QuoteActionPage() {
  const { quoteId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');
  const actionType = searchParams.get('action');
  
  const [quote, setQuote] = useState(null);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [actionCompleted, setActionCompleted] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [comments, setComments] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (quoteId && token) {
      fetchQuote();
    } else {
      setError('Invalid link. Please use the link from your email.');
      setLoading(false);
    }
  }, [quoteId, token]);

  const fetchQuote = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/quote/${quoteId}?token=${token}`);
      setQuote(response.data.quote);
      setCompany(response.data.company);
      
      // Check if action was already completed
      if (response.data.already_actioned) {
        setActionCompleted(true);
        setResultMessage(response.data.message);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to load quote. The link may be invalid or expired.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/public/quote/${quoteId}/approve`, { token });
      setResultMessage(response.data.message);
      setActionCompleted(true);
      toast.success('Quote approved!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve quote');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestChanges = async () => {
    if (!comments.trim()) {
      toast.error('Please describe the changes you need');
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/public/quote/${quoteId}/request-changes`, { 
        token, 
        comments 
      });
      setResultMessage(response.data.message);
      setActionCompleted(true);
      toast.success('Change request submitted!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/public/quote/${quoteId}/reject`, { 
        token, 
        reason 
      });
      setResultMessage(response.data.message);
      setActionCompleted(true);
      toast.success('Quote declined');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline quote');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading quote...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Link Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <p className="text-sm text-gray-500">
              If you believe this is an error, please contact us for assistance.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (actionCompleted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full shadow-xl">
          <CardContent className="p-8 text-center">
            {actionType === 'approve' && (
              <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto mb-4" />
            )}
            {actionType === 'request_changes' && (
              <Edit3 className="w-20 h-20 text-amber-500 mx-auto mb-4" />
            )}
            {actionType === 'reject' && (
              <XCircle className="w-20 h-20 text-gray-400 mx-auto mb-4" />
            )}
            
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              {actionType === 'approve' && 'Quote Approved!'}
              {actionType === 'request_changes' && 'Request Submitted'}
              {actionType === 'reject' && 'Quote Declined'}
            </h2>
            
            <p className="text-gray-600 mb-6">{resultMessage}</p>
            
            {actionType === 'approve' && (
              <div className="bg-green-50 p-4 rounded-lg text-left mb-6">
                <p className="text-green-800 text-sm">
                  <strong>What's next?</strong><br />
                  Our team will contact you shortly to schedule your service appointment.
                </p>
              </div>
            )}
            
            <p className="text-sm text-gray-500">
              Thank you for choosing {company.company_name || 'our services'}!
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already handled quote status
  if (quote?.status === 'approved' || quote?.status === 'accepted' || quote?.status === 'converted') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Quote Already Approved</h2>
            <p className="text-gray-600">
              This quote has already been approved. Our team will contact you shortly.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (quote?.status === 'rejected') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Quote Declined</h2>
            <p className="text-gray-600">
              This quote has been declined. If you'd like a new quote, please contact us.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Header - Same style as PDF */}
      <div className="bg-white border-b-4 border-teal-600 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-5">
          <div className="flex items-center gap-4">
            {/* Logo */}
            <img 
              src="https://customer-assets.emergentagent.com/job_a43a1576-2447-420f-b8ba-eeae8c53cffe/artifacts/a7oiuv0k_Black%20-%20With%20ROC%20number.png"
              alt="Redefyne Logo"
              className="w-20 h-20 object-contain"
            />
            {/* Company Info */}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Redefyne- Interior and Exterior</h1>
              <p className="text-gray-500 text-sm mt-1">
                2301 South Stearman Drive | Chandler, Arizona 85286
              </p>
              <p className="text-gray-500 text-sm">
                602-715-1313 | support@redefyneaz.com | Redefyneaz.com
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 py-8">
        {/* Quote Box - Same style as PDF */}
        <div className="flex flex-col md:flex-row gap-6 mb-6">
          {/* Recipient Section */}
          <div className="flex-1">
            <p className="text-sm font-bold text-gray-900 mb-3">RECIPIENT:</p>
            <p className="text-lg font-bold text-gray-900">{quote?.customer_name}</p>
            {quote?.service_address && (
              <p className="text-gray-600 text-sm mt-1">{quote.service_address}</p>
            )}
            {quote?.customer_email && (
              <p className="text-gray-500 text-sm">{quote.customer_email}</p>
            )}
            {quote?.customer_phone && (
              <p className="text-gray-500 text-sm">{quote.customer_phone}</p>
            )}
          </div>
          
          {/* Quote Summary Box */}
          <div className="w-full md:w-72 border rounded-lg overflow-hidden shadow-sm">
            <div className="bg-gray-600 text-white px-4 py-3">
              <span className="font-bold text-lg">Quote #{quote?.quote_number || quoteId?.slice(0, 8).toUpperCase()}</span>
            </div>
            <div className="bg-white">
              <div className="flex justify-between px-4 py-3 border-b">
                <span className="text-gray-500">Date</span>
                <span className="text-gray-900">{quote?.created_at ? formatDate(quote.created_at) : 'N/A'}</span>
              </div>
              <div className="flex justify-between px-4 py-3 border-b">
                <span className="text-gray-500">Valid Until</span>
                <span className="text-gray-900">{quote?.valid_until ? formatDate(quote.valid_until) : 'N/A'}</span>
              </div>
              <div className="bg-gray-600 text-white flex justify-between px-4 py-3">
                <span className="font-bold">Total</span>
                <span className="font-bold text-lg">${quote?.total_amount?.toFixed(2) || '0.00'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quote Details Card */}
        <Card className="mb-6 shadow-lg">
          <CardHeader className="border-b bg-gray-50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl">Line Items</CardTitle>
              <Badge variant="outline" className="text-teal-600 border-teal-600">
                {quote?.status?.replace('_', ' ').toUpperCase()}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {/* Line Items */}
            <div className="border rounded-lg overflow-hidden mb-6">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-3 font-medium text-gray-600">Product / Service</th>
                    <th className="text-center p-3 font-medium text-gray-600">Qty</th>
                    <th className="text-right p-3 font-medium text-gray-600">Unit Price</th>
                    <th className="text-right p-3 font-medium text-gray-600">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {quote?.items?.map((item, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="p-3">
                        <div className="font-medium">{item.name}</div>
                        {item.description && (
                          <div className="text-gray-500 text-xs mt-1">{item.description}</div>
                        )}
                        {item.is_optional && (
                          <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs mt-1">
                            Optional
                          </Badge>
                        )}
                      </td>
                      <td className="p-3 text-center">{item.quantity}</td>
                      <td className="p-3 text-right">{formatCurrency(item.unit_price)}</td>
                      <td className="p-3 text-right font-medium">{formatCurrency(item.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="space-y-2">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>{formatCurrency(quote?.subtotal)}</span>
                </div>
                {quote?.discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount {quote?.discount_percent > 0 && `(${quote.discount_percent}%)`}</span>
                    <span>-{formatCurrency(quote.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-600">
                  <span>Tax ({quote?.tax_percent || 0}%)</span>
                  <span>{formatCurrency(quote?.tax)}</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-teal-600 pt-2 border-t">
                  <span>Grand Total</span>
                  <span>{formatCurrency(quote?.total_amount)}</span>
                </div>
              </div>
            </div>

            {/* Notes */}
            {quote?.notes && (
              <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-amber-800 text-sm">
                  <strong>Notes:</strong> {quote.notes}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Section */}
        <Card className="shadow-lg">
          <CardHeader className="border-b">
            <CardTitle className="text-lg">
              {actionType === 'approve' && '✓ Approve This Quote'}
              {actionType === 'request_changes' && '✏️ Request Changes'}
              {actionType === 'reject' && 'Decline Quote'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {actionType === 'approve' && (
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-green-800">
                    By approving this quote, you agree to the services and pricing listed above. 
                    Our team will contact you to schedule your service appointment.
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button 
                    onClick={handleApprove} 
                    disabled={submitting}
                    className="flex-1 bg-green-600 hover:bg-green-700 h-12 text-lg"
                  >
                    {submitting ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
                    ) : (
                      <><CheckCircle2 className="w-5 h-5 mr-2" /> Approve Quote</>
                    )}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate(`/quote-action/${quoteId}?token=${token}&action=request_changes`)}
                  >
                    Request Changes Instead
                  </Button>
                </div>
              </div>
            )}

            {actionType === 'request_changes' && (
              <div className="space-y-4">
                <p className="text-gray-600">
                  Please describe the changes you'd like us to make to this quote:
                </p>
                <Textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="E.g., I'd like to add/remove certain services, adjust quantities, or discuss pricing..."
                  rows={5}
                  className="resize-none"
                />
                <div className="flex gap-3">
                  <Button 
                    onClick={handleRequestChanges} 
                    disabled={submitting || !comments.trim()}
                    className="flex-1 bg-amber-500 hover:bg-amber-600 h-12"
                  >
                    {submitting ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...</>
                    ) : (
                      <><Edit3 className="w-5 h-5 mr-2" /> Submit Change Request</>
                    )}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate(`/quote-action/${quoteId}?token=${token}&action=approve`)}
                  >
                    Approve Instead
                  </Button>
                </div>
              </div>
            )}

            {actionType === 'reject' && (
              <div className="space-y-4">
                <p className="text-gray-600">
                  We're sorry to see you go. Would you mind sharing why you're declining? (Optional)
                </p>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="E.g., Found a better price elsewhere, decided to postpone the project, etc."
                  rows={4}
                  className="resize-none"
                />
                <div className="flex gap-3">
                  <Button 
                    onClick={handleReject} 
                    disabled={submitting}
                    variant="outline"
                    className="flex-1 h-12 border-gray-300 text-gray-600 hover:bg-gray-100"
                  >
                    {submitting ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
                    ) : (
                      <><XCircle className="w-5 h-5 mr-2" /> Decline Quote</>
                    )}
                  </Button>
                  <Button 
                    onClick={() => navigate(`/quote-action/${quoteId}?token=${token}&action=approve`)}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    Actually, I Want to Approve
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>Questions? Contact us at {company.contact_email || 'support@redefyneaz.com'}</p>
          <p className="mt-1">© {new Date().getFullYear()} {company.company_name || 'Redefyne'}. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
